import { useMutation } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router";

import { useAuth } from "./use-auth";
import { alert } from "../components/common/Alert";
import { getShouldAllowCopyAction } from "../components/common/multiselect/utils";
import { DELETE_NOTES } from "../components/notes/edit/api/mutations";
import { defaultErrorHandler } from "../utils/operationTracking";

const noteMultiselectContext = createContext();

export const useNoteMultiselectContext = () => {
  return useContext(noteMultiselectContext);
};

/**
 * @typedef {Object} NoteMultiselectContext
 * @property {Object} selectedNoteUuids - an object with keys as uuids of the currently selected notes in state
 * @property {number} numSelectedNotes - count of selected notes
 * @property {boolean} shouldAllowCopyAction - determine if the "copy" button is disabled.
 * @property {boolean} shouldShowDeleteNoteModal - determine if the delete modal should show
 * @property {number} numPatientNotes - count of selected patient notes
 * @property {function} setSelectedNoteUuids - set state
 * @property {function} setNumSelectedNotes - set state
 * @property {function} setShouldAllowCopyAction - set state
 * @property {function} setShouldShowDeleteNoteModal - set state
 * @property {function} setNumPatientNotes - set state
 */

/**
 *
 * @returns {NoteMultiselectContext} context
 */
export function useProvideMultiselect(locationKey) {
  const [selectedNoteUuids, setSelectedNoteUuids] = useState({});
  const [numPatientNotes, setNumPatientNotes] = useState(0);
  const [numSelectedNotes, setNumSelectedNotes] = useState(0);
  const [shouldAllowCopyAction, setShouldAllowCopyAction] =
    useState(true);
  const [shouldShowDeleteNoteModal, setShouldShowDeleteNoteModal] =
    useState(false);

  // State-during-render: reset selection when navigating to a different page
  const [prevLocationKey, setPrevLocationKey] = useState(locationKey);
  if (locationKey !== prevLocationKey) {
    setPrevLocationKey(locationKey);
    setSelectedNoteUuids({});
    setNumSelectedNotes(0);
    setNumPatientNotes(0);
  }

  const resetSelectedNotesList = useCallback(() => {
    setSelectedNoteUuids({});
    setNumSelectedNotes(0);
    setNumPatientNotes(0);
  }, []);

  return {
    selectedNoteUuids,
    numSelectedNotes,
    shouldAllowCopyAction,
    shouldShowDeleteNoteModal,
    numPatientNotes,
    setSelectedNoteUuids,
    setNumSelectedNotes,
    setShouldAllowCopyAction,
    setShouldShowDeleteNoteModal,
    resetSelectedNotesList,
    setNumPatientNotes,
  };
}

// Available to any child component that calls useNoteMultiselectContext().
export function NoteMultiselectProvider({ children }) {
  const location = useLocation();
  const noteMultiselect = useProvideMultiselect(location.key);

  return (
    <noteMultiselectContext.Provider value={noteMultiselect}>
      {children}
    </noteMultiselectContext.Provider>
  );
}

export function useNoteMultiselect({ notes, refetch }) {
  const {
    shouldAllowCopyAction,
    setShouldAllowCopyAction,
    selectedNoteUuids,
    numSelectedNotes,
    setShouldShowDeleteNoteModal,
    resetSelectedNotesList,
  } = useNoteMultiselectContext();

  const [deleteNotes, { loading }] = useMutation(DELETE_NOTES);
  const { userUuid } = useAuth();

  useEffect(() => {
    if (!notes) {
      return;
    }

    // Check within selectedNoteUuids if any notes are not able to be copied.
    // filters for notes that are selected and are not submitted or new recordings.

    const nextShouldAllowCopyAction = getShouldAllowCopyAction({
      notes,
      selectedNoteUuids,
      numSelectedNotes,
    });

    if (shouldAllowCopyAction !== nextShouldAllowCopyAction) {
      // Avoid updating state unless necessary (ie, has changed)
      setShouldAllowCopyAction(nextShouldAllowCopyAction);
    }
  }, [
    selectedNoteUuids,
    numSelectedNotes,
    notes,
    setShouldAllowCopyAction,
    shouldAllowCopyAction,
  ]);

  function handleDeleteNotes() {
    deleteNotes({
      variables: { noteUuids: Object.keys(selectedNoteUuids) },
    })
      .then(() => {
        mixpanel.track("Notes Deleted", {
          noteUuids: Object.keys(selectedNoteUuids),
          userUuid,
        });
        setShouldShowDeleteNoteModal(false);
        resetSelectedNotesList();

        alert(
          "success",
          `Note${
            numSelectedNotes > 1 ? "s" : ""
          } deleted successfully.`,
        );
        refetch();
      })
      .catch(defaultErrorHandler);
  }

  return {
    handleDeleteNotes,
    resetSelectedNotesList,
    loading,
    shouldAllowCopyAction,
  };
}
